import { jwtDecode } from 'jwt-decode'

import { getAccessToken } from '@/contexts/auth/token-storage'
import { env } from '@/env'

import { type AuthTokens } from './types'

/**
 * Exchanges the refresh token for a new pair. Authenticated by the current
 * (possibly expired) access token, with the refresh token's `jti` in the body.
 *
 * Uses `fetch` rather than the shared `api` client on purpose: a failing
 * refresh must not re-enter the refresh interceptor.
 */
export async function refreshToken(
  refreshTokenJwt: string,
): Promise<AuthTokens> {
  const { jti } = jwtDecode<{ jti?: string }>(refreshTokenJwt)

  if (!jti) {
    throw new Error('Refresh token has no jti')
  }

  const accessToken = getAccessToken()

  const res = await fetch(
    new URL('/refresh-token', env.VITE_FINTALK_BACKOFFICE_URL),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-fintalk-origin': 'studio/backoffice',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify({
        grant_type: 'refresh_token',
        refresh_token: jti,
      }),
    },
  )

  if (!res.ok) {
    throw new Error(`Refresh token request failed [${res.status}]`)
  }

  return (await res.json()) as AuthTokens
}
